// Reading-progress bar for long pages (presentation only).
// A fixed 3px track at the top of the viewport; the fill is a child scaled with
// transform: scaleX(progress) (transform-origin 0 50%), never width, so it stays on the compositor.
//
//   data-reading="article" on main.page → progress through that element only:
//       clamp((scrollY - article top) / (article.offsetHeight - innerHeight), 0, 1)
//       It differs from the scrollbar, so the bar is role="progressbar" with aria-valuenow
//       updated sparingly (10% steps).
//   data-reading="page" → whole document: scrollTop / (scrollHeight - clientHeight). That only
//       duplicates the scrollbar, so the bar stays decorative (aria-hidden).
//
// Where scroll-driven animations are supported the fill is CSS-only (see 23-reading-progress.css):
// article → a view timeline on the article with animation-range: contain; page → scroll(root block).
// Otherwise a passive scroll listener, batched with requestAnimationFrame, sets the transform.
// The bar only shows when the article is clearly longer than the screen.
import { h } from './h.js';

const clamp01 = (x) => Math.min(1, Math.max(0, x));
export const cssDriven = () => {
  try { return !window.__RZ_READ_JS__ && !!CSS.supports('animation-timeline: view()') && !!CSS.supports('timeline-scope: --a'); } catch { return false; }
};

/** Progress (0…1) for the given article, or for the whole page when article is null. */
export function readingProgressValue(article) {
  if (!article) {
    const s = document.scrollingElement || document.documentElement;
    const max = s.scrollHeight - s.clientHeight;
    return max > 0 ? clamp01(s.scrollTop / max) : 0;
  }
  const top = article.getBoundingClientRect().top + window.scrollY; // = offsetTop relative to the document
  const range = article.offsetHeight - window.innerHeight;
  return range > 0 ? clamp01((window.scrollY - top) / range) : 1;
}

/** Mount the bar into root (next to the page, outside its blocks) if the page asks for one. */
export function mountReadingProgress(root) {
  const page = root.querySelector('main.page[data-reading]');
  if (!page) return null;
  const article = page.dataset.reading === 'article' ? page : null;
  const css = cssDriven();
  const fill = h('span', { class: 'rz-read-fill' });
  const bar = h('div', { class: `rz-read ${css ? 'is-css' : 'is-js'} ${article ? 'is-article' : 'is-page'}`, 'data-testid': 'reading-progress', 'data-cleanup': '', hidden: true }, fill);
  if (article) {
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-label', 'Reading progress');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    bar.setAttribute('aria-valuenow', '0');
    article.classList.add('rz-read-article'); // view-timeline: --rz-read
  } else bar.setAttribute('aria-hidden', 'true');
  root.classList.toggle('rz-read-scope', !!article);
  root.append(bar);

  let raf = 0; let step = -1;
  const update = () => {
    raf = 0;
    if (!bar.isConnected) return;
    // only long pages: at least half a screen more than the viewport
    const long = (article ? article.offsetHeight : document.documentElement.scrollHeight) - window.innerHeight > window.innerHeight * 0.5;
    if (bar.hidden === long) bar.hidden = !long;
    if (!long) return;
    const p = readingProgressValue(article);
    if (!css) fill.style.transform = `scaleX(${p.toFixed(4)})`;
    bar.dataset.progress = p.toFixed(3);
    if (article) {
      const s = Math.floor(p * 10 + 1e-6); // sparingly: 0, 10, 20 … 100 (never ahead of the reader)
      if (s !== step) { step = s; bar.setAttribute('aria-valuenow', String(s * 10)); }
    }
  };
  const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
  // the CSS path still needs the listener for aria-valuenow (article) and the length check
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
  bar.__cleanup = () => {
    window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll);
    if (raf) cancelAnimationFrame(raf);
    root.classList.remove('rz-read-scope');
  };
  return bar;
}
